// Резервациите на деня такива, каквито ги вижда екранът — чиста логика без
// React (тестове: settleClosedBills.test.ts).

import type { OpenTable, Reservation } from "../../../api/reservations";
import { followBills } from "./followBills";
import { isExpiredUpcoming, reservationBillIds, type FloorTable } from "./tableStatus";

/**
 * Настанена резервация, чиято сметка вече НЕ е сред отворените (закрита в
 * Маркиране), се показва като приключила: масата е свободна веднага. При
 * „Отделна сметка за всяка маса" — едва когато НИТО ЕДНА от сметките ѝ не е
 * отворена (огледало на BE синхронизацията).
 * BE я прехвърля в „Минали" при следващото четене — но сметка, закрита без
 * бон и с нулева сума, се изчаква до 90 s (картовото плащане може да я върне
 * отворена; `CLOSE_IN_FLIGHT_GRACE_S`). Изчакването е само за статуса ѝ в BE:
 * върне ли се сметката отворена, следващият отговор пак я носи настанена.
 * `releasedAt` не е известен → интервалът на заетост свършва в „сега".
 * Настанена без `billId` не се пипа (BE я приключва сам).
 */
export const settleClosedBills = (reservations: Reservation[], openTables: OpenTable[]): Reservation[] => {
  if (!reservations.some((reservation) => reservation.status === "seated")) return reservations;
  const openBillIds = new Set(openTables.map((bill) => bill.billId));
  let changed = false;
  const settled = reservations.map((reservation) => {
    const billIds = reservationBillIds(reservation);
    if (reservation.status !== "seated" || billIds.length === 0 || billIds.some((billId) => openBillIds.has(billId))) {
      return reservation;
    }
    changed = true;
    return { ...reservation, status: "completed" as const, releasedAt: null };
  });
  return changed ? settled : reservations;
};

/**
 * Предстояща, чийто край е минал (не е настанена, нито отказана), се показва
 * като „Неизпълнена" веднага — BE я прехвърля в "expired" при следващото
 * четене. Закъсняла, докато часът ѝ тече, остава предстояща.
 */
export const expireFinished = (reservations: Reservation[], nowMs: number): Reservation[] => {
  if (!reservations.some((reservation) => isExpiredUpcoming(reservation, nowMs))) return reservations;
  return reservations.map((reservation) =>
    isExpiredUpcoming(reservation, nowMs) ? { ...reservation, status: "expired" as const } : reservation,
  );
};

/**
 * Резервациите на деня за екрана: изтеклите предстоящи стават „Неизпълнена"
 * (`expireFinished`), закритите сметки приключват (`settleClosedBills`),
 * настанените следват преместената си сметка (`followBills`). Общо за
 * платното, списъка и проверката при „Настани".
 */
export const deriveReservations = (
  reservations: Reservation[],
  openTables: OpenTable[],
  tables: FloorTable[],
  nowMs: number,
): Reservation[] =>
  followBills(settleClosedBills(expireFinished(reservations, nowMs), openTables), openTables, tables);
